import { useEffect, useState } from "react"
import QRCode from "qrcode"
import { api } from "../../api/client.ts"
import { useApiQuery, useAsyncAction } from "../../hooks/index.ts"
import { useI18n } from "../../i18n/index.tsx"
import { Alert, Button, Card, CardHeader, EmptyState, Field, Input } from "../../ui/index.ts"
import { AppDownloadButton } from "../scan/AppDownloadButton.tsx"

/**
 * 스캐너 기기 관리.
 *
 * 창고 폰에 앱을 깔고 여기서 만든 QR 을 한 번 읽히면, 그 뒤로 앱은 **웹을 거치지 않고**
 * 스스로 입고 등록을 한다. 기기를 잃어버리면 그 기기만 끊는다 — 계정 비밀번호는 그대로다.
 *
 * **토큰은 만들 때 한 번만 보인다.** 서버에는 해시만 남아 다시 보여 줄 수 없다.
 */
type Device = {
  id: number
  name: string
  createdAt: string
  lastUsedAt: string | null
  revokedAt: string | null
}

type Made = { device: Device; token: string; pairingUrl: string }

export function ScanDevices() {
  const { t, locale } = useI18n()
  const devices = useApiQuery<{ items: Device[] }>("/api/scan-devices")
  const action = useAsyncAction()
  const [name, setName] = useState("")
  const [made, setMade] = useState<Made | null>(null)
  const [qr, setQr] = useState<string | null>(null)

  // QR 은 화면에서만 만든다 — 토큰이 서버 로그나 주소창을 거치지 않게.
  useEffect(() => {
    if (!made) {
      setQr(null)
      return
    }
    let alive = true
    void QRCode.toDataURL(made.pairingUrl, { width: 320, margin: 1 }).then((url) => {
      if (alive) setQr(url)
    })
    return () => {
      alive = false
    }
  }, [made])

  const when = (value: string | null) =>
    value ? new Date(value).toLocaleString(locale === "ja" ? "ja-JP" : "ko-KR") : "—"

  const items = devices.data?.items ?? []

  return (
    <Card padded={false} className="scan-devices">
      <CardHeader title={t("devices.title")} icon="scan" description={t("devices.description")} />

      <div className="scan-devices__body">
        {/* 앱 받기와 기기 묶기는 한자리에서 끝난다. */}
        <AppDownloadButton />
        {made ? (
          <Alert tone="info" title={t("devices.newTitle")}>
            <p>{t("devices.newHint")}</p>
            {qr ? <img className="scan-devices__qr" src={qr} alt={t("devices.qrAlt")} /> : null}
            <p className="scan-devices__once">{t("devices.onceOnly")}</p>
            <Button size="sm" onClick={() => setMade(null)}>
              {t("common.close")}
            </Button>
          </Alert>
        ) : (
          <form
            className="scan-devices__new"
            onSubmit={(event) => {
              event.preventDefault()
              void action.run(async () => {
                setMade(await api.post<Made>("/api/scan-devices", { name }))
                setName("")
                await devices.reload()
              })
            }}
          >
            <Field label={t("devices.name")} htmlFor="device-name">
              <Input
                id="device-name"
                value={name}
                placeholder={t("devices.namePlaceholder")}
                maxLength={40}
                onChange={(event) => setName(event.target.value)}
              />
            </Field>
            <Button type="submit" variant="primary" icon="plus" loading={action.busy}>
              {t("devices.add")}
            </Button>
          </form>
        )}

        {items.length === 0 ? (
          <EmptyState icon="scan" title={t("devices.empty")} />
        ) : (
          <ul className="scan-devices__list">
            {items.map((device) => (
              <li key={device.id} className="scan-devices__item" data-revoked={!!device.revokedAt}>
                <div>
                  <strong>{device.name}</strong>
                  <p className="muted">
                    {t("devices.lastUsed")}: {when(device.lastUsedAt)}
                  </p>
                </div>
                {device.revokedAt ? (
                  <span className="muted">{t("devices.revoked")}</span>
                ) : (
                  <Button
                    size="sm"
                    variant="danger"
                    icon="trash"
                    onClick={() =>
                      void action.run(async () => {
                        await api.del(`/api/scan-devices/${device.id}`)
                        await devices.reload()
                      })
                    }
                  >
                    {t("devices.revoke")}
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </Card>
  )
}
